import React from 'react';
import PropTypes from 'prop-types';

import { Box, Stack, Button, Accent, Text } from '@nerdwallet/currency';
import classNames from 'classnames';

import _ from 'lodash/';

import {
  stringSelectSpec,
  stringInputSpec,
  stringTextAreaSpec,
  boolCheckboxSpec,
} from '../../../lib/editing-configs';
import BackgroundColor from '../../background-color/background-color';
import { CARD_TREATMENT_ENUM, ALIGNMENT_ENUM } from '../lib/constants';
import parseMarkdownLinks from '../../../lib/parse-md-links';
import { getButtonStyle } from '../../../lib/button-style-helper';

import '../flex-card.less';

const isValid = (v) => !_.isNil(v) && !_.isEmpty(v);

const contentSizeEnum = {
  2: 'default',
  1: 'small',
};

const titleSizeEnum = {
  2: 'default',
  3: 'large',
};

const FlexCardItem = ({
  cardImage,
  bgOptions,
  eyebrow,
  cardAlignment,
  title,
  titleSize,
  subhead,
  subheadSize,
  label,
  ctaText,
  ctaHref,
  ctaType,
  desktopCarousel,
  shadowColorTreatment,
  borderSizeTreatment,
  borderColorTreatment,
  cardTreatment,
  textColor,
  parseLinks,
  fixImageHeight,
  imageHeight,
}) => {
  const linkParser = (content) =>
    parseLinks ? parseMarkdownLinks(content, false) : content;

  const borderTreatment =
    cardTreatment === CARD_TREATMENT_ENUM.BORDER
      ? {
          border: `${borderSizeTreatment} solid ${borderColorTreatment}`,
        }
      : '';

  const shadowTreatment =
    cardTreatment === CARD_TREATMENT_ENUM.BOX_SHADOW
      ? {
          boxShadow: `0px 1px 10px -1px ${shadowColorTreatment}`,
        }
      : '';
  const imageHeightStyle = fixImageHeight
    ? { height: imageHeight, overflow: 'hidden' }
    : {};

  const buttonStyle = getButtonStyle(ctaType);

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <Box
          padding="2"
          className={classNames('flex-card-item', {
            carousel: desktopCarousel,
          })}
        >
          <Box
            borderRadius="2"
            height="100"
            style={{
              backgroundColor,
              ...borderTreatment,
              ...shadowTreatment,
            }}
            className="flex-card-container"
          >
            <Box>
              {isValid(cardImage) && (
                <Box style={imageHeightStyle}>
                  <img
                    alt={title}
                    src={cardImage}
                    className="flex-card-image"
                  />
                </Box>
              )}
              <Box
                style={textColor}
                className="flex-card-content"
                paddingX="4"
                paddingTop="4"
              >
                <Stack spacing="3">
                  {isValid(eyebrow) && (
                    <Box paddingY="2">
                      <Accent align={cardAlignment} size="small">
                        {eyebrow}
                      </Accent>
                    </Box>
                  )}
                  <Text
                    align={cardAlignment}
                    component="h2"
                    size={titleSizeEnum[titleSize]}
                    bold
                  >
                    {title}
                  </Text>
                  <Text
                    align={cardAlignment}
                    component="p"
                    size={contentSizeEnum[subheadSize]}
                  >
                    {linkParser(subhead)}
                  </Text>
                </Stack>
              </Box>
            </Box>
            <Box paddingX="4" paddingBottom="4">
              <Stack spacing="3">
                {isValid(label) && (
                  <Box paddingTop="4" style={textColor}>
                    <Text size="small" align={cardAlignment} component="p">
                      {linkParser(label)}
                    </Text>
                  </Box>
                )}
                {ctaText && ctaHref && (
                  <Box align={cardAlignment} paddingTop="2">
                    <Button {...buttonStyle} href={ctaHref}>
                      {ctaText}
                    </Button>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      )}
    </BackgroundColor>
  );
};

FlexCardItem.getEditingConfig = (componentDocument) => {
  const optionalImageHeight = _.get(
    componentDocument,
    'props.fixImageHeight',
    false
  )
    ? [
        stringInputSpec(
          'imageHeight',
          'Image fixed height in px (default is 30px)'
        ),
      ]
    : [];
  return [
    stringInputSpec('title', 'Card Title'),
    stringSelectSpec('titleSize', 'Card Title Size', [
      { label: 'Default', value: '2' },
      { label: 'Large', value: '3' },
    ]),
    stringInputSpec('cardImage', 'Image Url'),
    boolCheckboxSpec('fixImageHeight', 'Set Card image height?'),
    ...optionalImageHeight,
    ...BackgroundColor.getEditingConfig(componentDocument),
    stringInputSpec('eyebrow', 'Card Eyebrow Text'),
    stringTextAreaSpec('subhead', 'Card Text Content', {
      help: `Want a hyperlink? Write it like this: [Hi](www.thing.com). NW links should be relative, e.g., [Home](/home)`,
    }),
    stringSelectSpec('subheadSize', 'Card Text Content Size', [
      { label: 'Default', value: '2' },
      { label: 'Small', value: '1' },
    ]),
    stringTextAreaSpec('label', 'Card label', {
      help: `Want a hyperlink? Write it like this: [Hi!](https://www.thing.com).
            NW links should be relative, e.g., [Home](/home)`,
    }),
    stringInputSpec('ctaText', 'Action Button Label'),
    stringInputSpec('ctaHref', 'Action Button URL'),
    stringSelectSpec('ctaType', 'Action Button Style', [
      { label: 'Primary', value: 'primary' },
      { label: 'Secondary', value: 'secondary' },
      { label: 'Tertiary', value: 'tertiary' },
    ]),
    stringSelectSpec('cardAlignment', 'Card Text Alignment', [
      { label: 'Left', value: ALIGNMENT_ENUM.LEFT },
      { label: 'Center', value: ALIGNMENT_ENUM.CENTER },
    ]),
  ];
};

FlexCardItem.displayName = 'FlexCardItem';

export default FlexCardItem;

FlexCardItem.propTypes = {
  size: PropTypes.string,
  borderSizeTreatment: PropTypes.string,
  borderColorTreatment: PropTypes.string,
  cardImage: PropTypes.string,
  cardTreatment: PropTypes.string,
  shadowColorTreatment: PropTypes.string,
  ...BackgroundColor.propTypes,
  title: PropTypes.string,
  eyebrow: PropTypes.string,
  label: PropTypes.string,
  subhead: PropTypes.string,
  subheadSize: PropTypes.string,
  titleSize: PropTypes.string,
  ctaType: PropTypes.string,
  ctaText: PropTypes.string,
  cardAlignment: PropTypes.string,
  ctaHref: PropTypes.string,
  cardsPerRow: PropTypes.string,
  fixImageHeight: PropTypes.bool,
  desktopCarousel: PropTypes.bool,
  imageHeight: PropTypes.string,
};

FlexCardItem.defaultProps = {
  size: 'sm',
  cardTreatment: CARD_TREATMENT_ENUM.NONE,
  shadowColorTreatment: '',
  ...BackgroundColor.defaultProps,
  title: 'Title goes here',
  subheadSize: '2',
  titleSize: '2',
  ctaType: 'primary',
  fixImageHeight: false,
  parseLinks: true,
  imageHeight: '30px',
  cardAlignment: ALIGNMENT_ENUM.LEFT,
};
